import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import { Peg } from "./Peg";
import { DroppingCoin } from "./DroppingCoin";
import { DroppingCoinDesktop } from "../DroppingCoinDesktop";
import type { PegPosition } from "../types/schema";

interface PlinkoBoardProps {
  isDropping: boolean;
  onCoinLanded: (slotIndex: number) => void;
  targetSlot?: number;
}

export const PlinkoBoard = ({
  isDropping,
  onCoinLanded,
  targetSlot,
}: PlinkoBoardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isMidTablet = useMediaQuery("(max-width: 1024px)");

  // ✅ Definiši screenWidth da izbjegnemo TS grešku
  const screenWidth = typeof window !== "undefined" ? window.innerWidth : 0;

  // 🔸 1. Dinamične dimenzije
  const boardWidth = isMobile
    ? 300
    : isMidTablet
    ? Math.min(450, screenWidth * 0.9)
    : 600;

  const boardHeight = isMobile ? 400 : isMidTablet ? 450 : 600;
  const pegSize = isMobile ? 12 : 20;
  const horizontalGap = isMobile ? 35 : 60;
  const verticalGap = isMobile ? 32 : 48;

  // 🔸 2. Pegovi se sada računaju kad god se promijeni layout
  const pegs = useMemo<PegPosition[]>(() => {
    const pegArray: PegPosition[] = [];
    const rows = 10;
    const startY = 120;

    for (let row = 0; row < rows; row++) {
      const pegsInRow = row + 2;
      const rowWidth = (pegsInRow - 1) * horizontalGap;
      const startX = (boardWidth - rowWidth) / 2;

      for (let col = 0; col < pegsInRow; col++) {
        pegArray.push({
          x: startX + col * horizontalGap,
          y: startY + row * verticalGap,
          id: `peg-${row}-${col}`,
        });
      }
    }

    return pegArray;
  }, [boardWidth, horizontalGap, verticalGap, isMobile]); // ✅ dodani dependency

  return (
    <Box
      sx={{
        position: "relative",
        width: boardWidth,
        maxWidth: "90vw", // ⬅️ dodatno osiguranje
        height: boardHeight,
        margin: "0 auto",
        zIndex: 2,
      }}
    >
      {/* 🔸 Render pegs */}
      {pegs.map((peg) => (
        <Peg key={peg.id} x={peg.x} y={peg.y} size={pegSize} />
      ))}

      {/* 🔸 Render dropping coin */}
      {isDropping && (
        <>
          {isMobile ? (
            <DroppingCoin
              startX={boardWidth / 2}
              startY={50}
              pegs={pegs}
              onLanded={onCoinLanded}
              slotWidth={60}
              slotCount={10}
              boardWidth={boardWidth}
              targetSlot={targetSlot}
            />
          ) : (
            <DroppingCoinDesktop
              startX={boardWidth / 2}
              startY={50}
              pegs={pegs}
              onLanded={onCoinLanded}
              slotWidth={60}
              slotCount={10}
              boardWidth={boardWidth}
              targetSlot={targetSlot}
            />
          )}
        </>
      )}
    </Box>
  );
};
